/**
 * Ask the case (PIVOT01, PIVOT02): the officer asks one bounded question of a finished run record and of the case's
 * transfer ledger. The model reads the ledger first (what the run read, with hashes, no page budget) and walks live only
 * where the ledger does not cover.
 *
 * PIVOT02 added: the answers' provenance chips (a fact id resolves to the record row, a PV- id or a hash to the ledger or
 * page-read row with its hash), the rows each tool found as a table, and a "Promote to graph" action per row. Promotion
 * is the `promote_pivot_rows` operator and runs only on the officer's click; the panel then reloads the stored pivots,
 * whose `promoted` list says which rows are on the graph.
 *
 * A question box with the two budgets shown (seconds of wall clock, page reads), a Run button, the live job progress
 * (the canvas job the `pivot` operator runs as, polled by `useCanvasJobWatch`) and the stored pivots of the run rendered
 * under their own heading "Officer pivot (lead)": the question, the answer sentences with citation chips and the ledger
 * lines (every page read with provider and time). A pivot is a lead for the officer to check, never a trace, a label or
 * a notice row. Officer tier and above only (`case.pivot`, R31); the server refuses a record that is not finished.
 *
 * R8: the load effect owns a `cancelled` flag, and the pivots shown are keyed by case and run.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, Play, Search } from 'lucide-react';
import { PivotAnswerCard } from './PivotAnswerCard';
import { casePivotService } from '../../services/api';
import { useFeature } from '../../auth/features';
import { useCanvasJobWatch } from '../cases/useCanvasJobWatch';
import { describeJobProgress, runErrorMessage } from '../cases/caseRuns';
import { PIVOT_TITLE } from './sections/pivot';
import {
  PIVOT_DEFAULT_PAGES, PIVOT_DEFAULT_SECONDS, PIVOT_MAX_PAGES, PIVOT_MAX_SECONDS, type PivotBlock, type PivotRow,
} from '../../types/pivot';

export interface PivotPanelProps {
  caseId: string;
  /** The run asked about: its event id, run id, or `latest`. */
  runRef: string;
  /** The pivots of this run as stored (the report prints them). */
  onBlocks?: (blocks: PivotBlock[], runRef: string) => void;
  /** Disable the Run button (a viewer collaborator reads the case and never acts on it). */
  readOnly?: boolean;
}

const clampInt = (v: string, lo: number, hi: number, fallback: number) => {
  const x = Math.floor(Number(v));
  return Number.isFinite(x) && x > 0 ? Math.min(hi, Math.max(lo, x)) : fallback;
};

export function PivotPanel({ caseId, runRef, onBlocks, readOnly = false }: PivotPanelProps) {
  const allowed = useFeature('case.pivot');
  const [question, setQuestion] = useState('');
  const [seconds, setSeconds] = useState(String(PIVOT_DEFAULT_SECONDS));
  const [pages, setPages] = useState(String(PIVOT_DEFAULT_PAGES));
  const [blocks, setBlocks] = useState<PivotBlock[]>([]);
  const [jobId, setJobId] = useState<string | null>(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState('');
  const [promote, setPromote] = useState<{ jobId: string; rowId: string; pivotId: string } | null>(null);
  const [promotedNow, setPromotedNow] = useState<Record<string, Set<string>>>({});
  const [notice, setNotice] = useState('');
  const onBlocksRef = useRef(onBlocks);
  onBlocksRef.current = onBlocks;

  const load = useCallback(async (isCancelled: () => boolean) => {
    try {
      const got = await casePivotService.list(caseId, runRef);
      if (isCancelled()) return;
      setBlocks(got);
      onBlocksRef.current?.(got, runRef);
    } catch (e) {
      if (isCancelled()) return;
      setBlocks([]);
      onBlocksRef.current?.([], runRef);
      const status = (e as { response?: { status?: number } })?.response?.status;
      if (status && status !== 403 && status !== 404) setError(runErrorMessage(e));
    }
  }, [caseId, runRef]);

  useEffect(() => {
    if (!allowed) return;
    let cancelled = false;
    setBlocks([]); setError(''); setJobId(null); setPromote(null); setPromotedNow({}); setNotice('');
    void load(() => cancelled);
    return () => { cancelled = true; };
  }, [allowed, load]);

  const watch = useCanvasJobWatch(jobId ? caseId : null, jobId, {
    onDone: (job) => {
      if (job.status === 'failed') setError(job.error || 'The pivot failed.');
      setJobId(null);
      void load(() => false);
    },
    onLost: (message) => { setError(message); setJobId(null); },
  });

  useCanvasJobWatch(promote ? caseId : null, promote?.jobId, {
    onDone: (job) => {
      const done = promote;
      setPromote(null);
      if (job.status === 'failed') { setError(job.error || 'The promotion failed.'); return; }
      if (done) {
        setPromotedNow((m) => ({ ...m, [done.pivotId]: new Set([...(m[done.pivotId] || []), done.rowId]) }));
        setNotice(`Row ${done.rowId} is on the graph (context wallet, pivot attribution). Open the canvas to see it.`);
      }
      void load(() => false);
    },
    onLost: (message) => { setError(message); setPromote(null); },
  });

  if (!allowed) return null;

  const running = starting || !!jobId;
  const s = clampInt(seconds, 5, PIVOT_MAX_SECONDS, PIVOT_DEFAULT_SECONDS);
  const p = clampInt(pages, 1, PIVOT_MAX_PAGES, PIVOT_DEFAULT_PAGES);
  const canRun = !readOnly && !running && question.trim().length > 0;

  const run = async () => {
    setError(''); setStarting(true);
    try {
      const env = await casePivotService.start(caseId, { run_id: runRef, question: question.trim(), budget: { seconds: s, pages: p } });
      setJobId(env.job_id);
    } catch (e) {
      setError(runErrorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  const startPromote = async (block: PivotBlock, row: PivotRow) => {
    setError(''); setNotice('');
    try {
      const env = await casePivotService.promote(caseId, { run_id: runRef, question_id: block.pivot_id, row_ids: [row.id] });
      setPromote({ jobId: env.job_id, rowId: row.id, pivotId: block.pivot_id });
    } catch (e) {
      setError(runErrorMessage(e));
    }
  };
  const promotedIds = (b: PivotBlock): Set<string> => new Set([
    ...(b.promoted || []).map((p) => p.row_id), ...(promotedNow[b.pivot_id] || []),
  ]);

  return (
    <section data-testid="pivot-panel" data-tour="case-ask-the-case" className="border-b border-border px-4 py-3 text-sm">
      <h3 className="flex items-center gap-2 font-medium"><Search className="w-4 h-4" /> Ask the case</h3>
      <p className="text-xs text-muted-foreground mt-1">
        One bounded question of this case. The model reads the case's transfer ledger first (everything the run read, with
        hashes, no page budget) and reads live pages only where the ledger does not cover, under the budget below. A transfer
        the ledger lacks was not read; it did not necessarily not happen. The answer is a lead to check, not a trace, a label
        or a notice row, and nothing reaches the graph until you promote a row.
      </p>
      <div className="mt-2 flex flex-wrap items-end gap-2">
        <label className="flex-1 min-w-[16rem] text-xs">
          <span className="sr-only">Question</span>
          <input
            data-testid="pivot-question-input"
            value={question}
            maxLength={600}
            disabled={running}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="e.g. which of the feeders also paid the hub, and when"
            className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
          />
        </label>
        <label className="text-xs">
          Seconds
          <input data-testid="pivot-seconds" type="number" min={5} max={PIVOT_MAX_SECONDS} value={seconds} disabled={running}
            onChange={(e) => setSeconds(e.target.value)} className="ml-1 w-16 rounded-md border border-border bg-background px-1.5 py-1" />
        </label>
        <label className="text-xs">
          Page reads
          <input data-testid="pivot-pages" type="number" min={1} max={PIVOT_MAX_PAGES} value={pages} disabled={running}
            onChange={(e) => setPages(e.target.value)} className="ml-1 w-16 rounded-md border border-border bg-background px-1.5 py-1" />
        </label>
        <button type="button" data-testid="pivot-run" disabled={!canRun} onClick={() => void run()}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground disabled:opacity-50">
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />} Run
        </button>
      </div>
      <p data-testid="pivot-budget-shown" className="mt-1 text-xs text-muted-foreground">
        Budget: {s} s and {p} page reads (at most {PIVOT_MAX_SECONDS} s and {PIVOT_MAX_PAGES}), up to 6 model rounds.
      </p>
      {running && (
        <p data-testid="pivot-progress" className="mt-2 flex items-center gap-2 text-xs">
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
          {describeJobProgress(watch.job?.progress) || (starting ? 'Starting the pivot' : 'Waiting for the pivot to start')}
        </p>
      )}
      {error && <p role="alert" data-testid="pivot-error" className="mt-2 text-xs text-red-500">{error}</p>}
      {notice && <p role="status" data-testid="pivot-notice" className="mt-2 text-xs text-green-600">{notice}</p>}
      {blocks.length > 0 && (
        <div className="mt-3" data-testid="pivot-results">
          <h4 className="font-medium">{PIVOT_TITLE}: past questions ({blocks.length})</h4>
          {blocks.map((b, i) => (
            <PivotAnswerCard key={b.pivot_id} block={b} defaultOpen={i === 0} promotedIds={promotedIds(b)}
              promotingRowId={promote ? promote.rowId : null} canPromote={!readOnly} onPromote={(blk, row) => void startPromote(blk, row)} />
          ))}
        </div>
      )}
    </section>
  );
}
